import { NodeType, parse, type Node } from "node-html-parser";

export interface FundingTemplateVariable {
  key: string;
  token: string;
  label: string;
  description: string;
}

/** Maps alternate placeholder names to canonical keys in values / send-time variables. */
export const FUNDING_TEMPLATE_PLACEHOLDER_ALIASES: Record<string, string> = {
  funder_contact_name: "contact_name",
};

/**
 * Merge tokens filled per funder when an outreach email is sent.
 * Club name, appeal title, target amount, etc. are written as plain text when drafting the template.
 */
export const FUNDING_SEND_TEMPLATE_VARIABLES: FundingTemplateVariable[] = [
  {
    key: "funder_name",
    token: "{{funder_name}}",
    label: "Funder name",
    description: "Organization or funder name.",
  },
  {
    key: "contact_name",
    token: "{{contact_name}}",
    label: "Contact name",
    description: "Named contact person at the funder.",
  },
  {
    key: "funder_contact_name",
    token: "{{funder_contact_name}}",
    label: "Contact name (alternate)",
    description: "Same value as {{contact_name}}.",
  },
  {
    key: "appeal_page_url",
    token: "{{appeal_page_url}}",
    label: "Appeal page URL",
    description:
      "Published club-directory link when available (empty until the landing page is approved). Use only in visible text, or use “Insert appeal page button” for a safe link.",
  },
];

/** Variables offered in the builder UI and to the AI template generator. */
export const FUNDING_TEMPLATE_VARIABLES = FUNDING_SEND_TEMPLATE_VARIABLES;

/** Shape passed as `context.template_variables` for funding appeal one-shot / AI generation. */
export function fundingSendTemplateVariablesForAiContext(): Array<{
  token: string;
  description: string;
}> {
  return FUNDING_SEND_TEMPLATE_VARIABLES.map(({ token, description }) => ({ token, description }));
}

/** Still substituted at send time if present in older or hand-edited templates; not offered in the picker. */
const FUNDING_TEMPLATE_OPTIONAL_SUBSTITUTION_KEYS: readonly string[] = [
  "club_name",
  "appeal_name",
  "appeal_target",
  "session_name",
  "player_count",
];

const FUNDING_TEMPLATE_LEGACY_PLACEHOLDER_KEYS: readonly string[] = ["goal_notes"];

const FUNDING_TEMPLATE_KNOWN_KEYS = new Set<string>([
  ...FUNDING_SEND_TEMPLATE_VARIABLES.map((variable) => variable.key),
  ...FUNDING_TEMPLATE_OPTIONAL_SUBSTITUTION_KEYS,
  ...FUNDING_TEMPLATE_LEGACY_PLACEHOLDER_KEYS,
]);

/** Whether `{{key}}` is a known funding merge token (including legacy keys and alias names). */
export function isKnownFundingTemplateTokenKey(key: string): boolean {
  const valueKey = FUNDING_TEMPLATE_PLACEHOLDER_ALIASES[key] ?? key;
  return FUNDING_TEMPLATE_KNOWN_KEYS.has(valueKey) || FUNDING_TEMPLATE_KNOWN_KEYS.has(key);
}

function substituteFundingTokensInPlainText(text: string, values: Record<string, string>): string {
  return text.replace(/\{\{([a-z_]+)\}\}/g, (fullMatch, key: string) => {
    if (!isKnownFundingTemplateTokenKey(key)) return fullMatch;
    const valueKey = FUNDING_TEMPLATE_PLACEHOLDER_ALIASES[key] ?? key;
    return values[valueKey] ?? values[key] ?? "";
  });
}

function walkReplaceTextNodes(node: Node, values: Record<string, string>): void {
  if (node.nodeType === NodeType.TEXT_NODE) {
    const text = node.rawText;
    const next = substituteFundingTokensInPlainText(text, values);
    if (next !== text) {
      node.rawText = next;
    }
    return;
  }
  for (const child of node.childNodes) {
    walkReplaceTextNodes(child, values);
  }
}

/** Replace `{{token}}` only in text nodes (never in attributes). */
export function applyFundingLetterTemplateTextNodesOnly(
  html: string,
  values: Record<string, string>,
): string {
  if (!html) return html;
  const root = parse(html, { comment: true });
  for (const child of root.childNodes) {
    walkReplaceTextNodes(child, values);
  }
  return root.toString();
}

/**
 * Apply merge tokens to HTML or plain text. Tokens are substituted only in text nodes so values never
 * replace attribute contents (for example `href="{{contact_name}}"` stays intact).
 */
export function applyFundingLetterTemplate(template: string, values: Record<string, string>): string {
  return applyFundingLetterTemplateTextNodesOnly(template, values);
}
